import React from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpRightFromSquare,
  faClipboardCheck,
  faListCheck,
  faSpinner,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import {
  formatTaskDate,
  getTaskForwardCount,
  getTaskPriorityClasses,
  getTaskStatusClasses,
  isTaskForwardLocked,
  normalizeTaskProgressPercent,
} from "../Tasks/taskUtils";

const BRAND_LR  = "linear-gradient(to right, #C04C2A, #4A1B13)";
const BRAND_135 = "linear-gradient(135deg, #C04C2A 0%, #8B2A15 55%, #4A1B13 100%)";

export default function TicketTaskListModal({ open, ticket, tasks, loading, error, onClose, onCreateTask }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center sm:items-center sm:p-3">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div
        className="relative z-10 flex w-full flex-col overflow-hidden bg-white dark:bg-gray-900 shadow-2xl"
        style={{ maxWidth: 680, maxHeight: "96dvh", borderRadius: "1.25rem 1.25rem 0 0" }}
      >
        {/* ── Header ── */}
        <div className="relative shrink-0 overflow-hidden px-4 py-4 text-white sm:px-6" style={{ background: BRAND_135 }}>
          <div className="pointer-events-none absolute -right-6 -top-6 h-28 w-28 rounded-full bg-white/5 blur-2xl" />

          <button
            type="button"
            onClick={onClose}
            className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full bg-white/15 text-white transition hover:bg-white/25"
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} className="text-xs" />
          </button>

          <div className="pr-8">
            <div className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-orange-100">
              <FontAwesomeIcon icon={faListCheck} />
              Ticket Task List
            </div>
            {ticket?.ticketNumber ? (
              <div className="mt-1 inline-flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/10 px-2 py-0.5 text-[10px] font-bold text-orange-200 ml-2">
                {ticket.ticketNumber}
              </div>
            ) : null}
            <h2 className="mt-1.5 line-clamp-1 text-base font-extrabold tracking-tight text-white">
              {ticket?.subject || "Ticket Task List"}
            </h2>
            <p className="mt-0.5 text-[11px] text-orange-200">
              {tasks.length} {tasks.length === 1 ? "task" : "tasks"} · Internal work items for this support ticket
            </p>
          </div>
        </div>

        {/* ── Task list ── */}
        <div className="flex-1 overflow-y-auto bg-slate-50/60 dark:bg-gray-900/60 px-4 py-3 sm:px-5">
          {loading ? (
            <div className="rounded-2xl border border-dashed border-orange-200 bg-orange-50/30 dark:border-gray-700 dark:bg-gray-800/30 px-4 py-12 text-center text-sm" style={{ color: "#C04C2A" }}>
              <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
              Loading ticket tasks…
            </div>
          ) : error ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">{error}</div>
          ) : tasks.length ? (
            <div className="space-y-2">
              {tasks.map((task) => {
                const progressPercent = normalizeTaskProgressPercent(task.progressPercent);
                const forwardCount = getTaskForwardCount(task);
                const forwardLocked = isTaskForwardLocked(task);

                return (
                  <div
                    key={task.id}
                    className="relative overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-gray-700 dark:bg-gray-800 shadow-sm"
                  >
                    {/* left accent bar */}
                    <div
                      className="absolute inset-y-0 left-0 w-[3px] rounded-l-xl"
                      style={{ background: task.status === "Completed" ? "#10b981" : BRAND_LR }}
                    />

                    <div className="flex flex-col gap-2.5 p-3 pl-4 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className={`rounded-md px-2 py-0.5 text-[10px] font-bold ${getTaskStatusClasses(task.status)}`}>{task.status}</span>
                          <span className={`rounded-md px-2 py-0.5 text-[10px] font-bold ${getTaskPriorityClasses(task.priority)}`}>{task.priority}</span>
                          <span className="rounded-md border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500 dark:border-gray-700 dark:bg-gray-700 dark:text-gray-300">
                            {progressPercent}%
                          </span>
                        </div>

                        <div className="mt-2 text-sm font-semibold text-slate-900 dark:text-white">{task.title || "Untitled task"}</div>
                        {task.description ? (
                          <p className="mt-1 line-clamp-2 text-[12px] leading-5 text-slate-500 dark:text-gray-400">{task.description}</p>
                        ) : null}

                        {/* Progress bar */}
                        <div className="mt-2">
                          <div className="h-1.5 overflow-hidden rounded-full bg-slate-200 dark:bg-gray-700">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{
                                width: `${progressPercent}%`,
                                background: progressPercent === 100 ? "linear-gradient(to right,#34d399,#10b981)" : BRAND_LR,
                              }}
                            />
                          </div>
                        </div>

                        <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]">
                          <span className="rounded-md border border-slate-200 bg-white px-2 py-0.5 font-bold text-slate-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
                            {forwardCount} {forwardCount === 1 ? "forward" : "forwards"}
                          </span>
                          <span className={`rounded-md px-2 py-0.5 font-bold ${forwardLocked ? "border border-amber-200 bg-amber-100 text-amber-700" : "border border-emerald-200 bg-emerald-100 text-emerald-700"}`}>
                            {forwardLocked ? "Forward locked" : "Owner active"}
                          </span>
                        </div>

                        <div className="mt-1.5 flex flex-wrap gap-x-3 text-[10px] text-slate-400">
                          <span>Assigned: {task.assignedEmployeeName || "Unassigned"}</span>
                          <span>Due: {formatTaskDate(task.dueDate)}</span>
                          <span>Updated: {formatTaskDate(task.updatedAt, true)}</span>
                        </div>
                      </div>

                      <div className="shrink-0 sm:pl-2">
                        <Link
                          to={task.assignedUserId ? `/my-tasks?userId=${encodeURIComponent(task.assignedUserId)}` : "/my-tasks"}
                          className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-orange-100 bg-orange-50 px-3 py-1.5 text-[11px] font-bold text-slate-700 transition hover:bg-orange-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 sm:w-auto"
                        >
                          Open Task
                          <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-[9px]" style={{ color: "#C04C2A" }} />
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div
              className="rounded-2xl border border-dashed px-4 py-12 text-center text-sm text-slate-400"
              style={{ borderColor: "rgba(192,76,42,0.2)", background: "rgba(192,76,42,0.02)" }}
            >
              <FontAwesomeIcon icon={faClipboardCheck} className="mr-2" style={{ color: "#C04C2A" }} />
              No task created from this ticket yet.
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="flex shrink-0 items-center justify-between border-t border-slate-100 bg-white dark:border-gray-800 dark:bg-gray-900 px-4 py-2.5 sm:px-5">
          <span className="text-[11px] text-slate-400">
            {ticket?.ticketNumber ? `Task workspace for ${ticket.ticketNumber}` : "Internal task workspace"}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-600 transition hover:bg-slate-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
            >
              Close
            </button>
            <button
              type="button"
              onClick={onCreateTask}
              className="inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-bold text-white transition hover:opacity-90"
              style={{ background: BRAND_LR }}
            >
              <FontAwesomeIcon icon={faClipboardCheck} />
              Make Task
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
